import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ArrowBack, Check, CheckCircle, Close, EmojiEvents, Favorite, FavoriteBorder,
    Groups, PersonOutline, Refresh, SentimentDissatisfied, Star
} from "@mui/icons-material";
import TeamLogo from "../components/TeamLogo";
import PlayerImage from "../components/PlayerImage";
import GameLeaderboardPanel from "../components/games/GameLeaderboardPanel";
import { divisionMappings } from "../Constants";
import {
    RunView,
    TeamRosterCandidate,
    TeamRosterRoundView,
    endTeamRosterRun,
    nextTeamRosterRound,
    startTeamRosterRun,
    submitTeamRosterGuess
} from "../services/MinigameService";
import styles from "./TeamRosterPage.module.css";

type Difficulty = "easy" | "hard";
type Phase = "loading" | "playing" | "won" | "lost";

const MAX_MISTAKES = 3;
const ROUND_SECS = 60;
const BEST_KEY = "teamRosterBest";

const coverStyle: React.CSSProperties = {
    width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block'
};

const TeamRosterPage: React.FC = () => {
    const { t } = useTranslation(["pages", "common"]);
    const [difficulty, setDifficulty] = useState<Difficulty>("easy");
    const [round, setRound] = useState<TeamRosterRoundView | null>(null);
    const [phase, setPhase] = useState<Phase>("loading");
    const [found, setFound] = useState<string[]>([]);
    const [wrong, setWrong] = useState<Set<string>>(new Set());
    const [mistakes, setMistakes] = useState(0);
    const [revealCorrect, setRevealCorrect] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const [streak, setStreak] = useState(0);
    const [best, setBest] = useState<number>(() => {
        const v = Number(localStorage.getItem(BEST_KEY));
        return Number.isFinite(v) ? v : 0;
    });
    const [elapsed, setElapsed] = useState(0);
    const [roundSecs, setRoundSecs] = useState(ROUND_SECS);
    // Bumped whenever the run's score can have changed, so the leaderboard panel refetches.
    const [boardKey, setBoardKey] = useState(0);
    // The team was lost to the clock rather than to mistakes.
    const [timeUp, setTimeUp] = useState(false);
    const startedAt = useRef<number>(Date.now());
    const roundSecsRef = useRef(ROUND_SECS);
    // The run in play. A full roster continues it; a lost team (or a new difficulty) starts another.
    const runRef = useRef<{ id: string; over: boolean } | null>(null);
    // Each load gets a number; a response for an older one (a quick difficulty/mode toggle) is ignored.
    const loadSeq = useRef(0);
    const [loadFailed, setLoadFailed] = useState(false);
    const phaseRef = useRef<Phase>("loading");
    phaseRef.current = phase;

    const applyRound = (view: RunView<TeamRosterRoundView>) => {
        runRef.current = { id: view.runId, over: false };
        setRound(view.round);
        setStreak(view.score);
        roundSecsRef.current = view.round.secondsLeft;
        setRoundSecs(view.round.secondsLeft);
        startedAt.current = Date.now();
        setElapsed(0);
        setPhase("playing");
    };

    const loadRound = useCallback(async (fresh: boolean) => {
        setPhase("loading");
        setFound([]);
        setWrong(new Set());
        setMistakes(0);
        setRevealCorrect(new Set());
        setBusy(false);
        setTimeUp(false);
        setLoadFailed(false);
        const seq = ++loadSeq.current;
        const run = runRef.current;
        const view = run && !run.over && !fresh
            ? await nextTeamRosterRound(run.id)
            : await startTeamRosterRun(difficulty);
        if (seq !== loadSeq.current) return;
        if (!view) {
            setLoadFailed(true);
            return;
        }
        applyRound(view);
    }, [difficulty]);

    // Reset the streak and start a fresh run whenever difficulty changes.
    useEffect(() => {
        setStreak(0);
        loadRound(true);
    }, [loadRound]);

    // Round timer — the team is lost when the clock runs out. The server holds the same deadline.
    useEffect(() => {
        const timer = setInterval(() => {
            if (phaseRef.current !== "playing") return;
            const el = Math.floor((Date.now() - startedAt.current) / 1000);
            setElapsed(Math.min(el, roundSecsRef.current));
            if (el >= roundSecsRef.current) endOnClock();
        }, 500);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatDivision = (d: string) => divisionMappings.get(d) || d;

    const endWin = (runScore: number, roster: TeamRosterCandidate[] | null) => {
        setPhase("won");
        if (roster) setRevealCorrect(new Set(roster.map((c) => c.steamId)));
        setStreak(runScore);
        setBest((b) => {
            if (runScore <= b) return b;
            localStorage.setItem(BEST_KEY, String(runScore));
            return runScore;
        });
        setBoardKey((k) => k + 1);
    };

    const endLoss = (roster: TeamRosterCandidate[] | null) => {
        if (runRef.current) runRef.current.over = true;
        setPhase("lost");
        setStreak(0);
        if (roster) setRevealCorrect(new Set(roster.map((c) => c.steamId)));
        setBoardKey((k) => k + 1);
    };

    // The clock ran out: end the run on the server, which reveals the roster.
    const endOnClock = async () => {
        if (phaseRef.current !== "playing") return;
        const run = runRef.current;
        setTimeUp(true);
        endLoss(null);
        if (!run) return;
        const roster = await endTeamRosterRun(run.id);
        if (roster) setRevealCorrect(new Set(roster.map((c) => c.steamId)));
    };

    const handlePick = async (steamId: string) => {
        const run = runRef.current;
        if (phase !== "playing" || busy || !run || !round) return;
        if (found.includes(steamId) || wrong.has(steamId)) return;
        setBusy(true);
        const res = await submitTeamRosterGuess(run.id, steamId);
        setBusy(false);
        // The clock may have run out while the guess was in flight.
        if (!res || phaseRef.current !== "playing") return;
        if (res.correct) {
            setFound((f) => [...f, steamId]);
            if (res.roundWon) endWin(res.score, res.reveal);
            return;
        }
        if (res.timedOut) setTimeUp(true);
        else setWrong((w) => new Set(w).add(steamId));
        setMistakes(MAX_MISTAKES - res.mistakesLeft);
        if (res.runOver) endLoss(res.reveal);
    };

    const over = phase === "won" || phase === "lost";
    const remaining = Math.max(0, roundSecs - elapsed);
    const timeText = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;

    const candidateClass = (c: TeamRosterCandidate): string => {
        if (found.includes(c.steamId)) return `${styles.candidate} ${styles.correct}`;
        if (wrong.has(c.steamId)) return `${styles.candidate} ${styles.wrong}`;
        if (over && revealCorrect.has(c.steamId)) return `${styles.candidate} ${styles.missed}`;
        if (over) return `${styles.candidate} ${styles.dimmed}`;
        return styles.candidate;
    };

    const candidateBadge = (c: TeamRosterCandidate) => {
        if (found.includes(c.steamId)) {
            return <span className={`${styles.candBadge} ${styles.badgeCorrect}`}><Check sx={{ fontSize: 16 }} /></span>;
        }
        if (wrong.has(c.steamId)) {
            return <span className={`${styles.candBadge} ${styles.badgeWrong}`}><Close sx={{ fontSize: 16 }} /></span>;
        }
        if (over && revealCorrect.has(c.steamId)) {
            return <span className={`${styles.candBadge} ${styles.badgeMissed}`}><Star sx={{ fontSize: 16 }} /></span>;
        }
        return null;
    };

    return (
        <div className={styles.page}>
            <Link to="/games" className={styles.backLink}>
                <ArrowBack sx={{ fontSize: 18 }} />
                {t('teamRoster.back')}
            </Link>

            {/* ===== Header ===== */}
            <div className={styles.header}>
                <div className={styles.identity}>
                    <span className={styles.gameIcon}>
                        <Groups sx={{ fontSize: 28 }} />
                    </span>
                    <div>
                        <h1 className={styles.title}>Team Roster</h1>
                        <p className={styles.tagline}>{t('teamRoster.tagline')}</p>
                    </div>
                </div>
                <div className={styles.toggleGroup}>
                    <button
                        className={`${styles.toggleBtn} ${difficulty === "easy" ? styles.toggleBtnActive : ""}`}
                        onClick={() => setDifficulty("easy")}
                    >
                        {t('teamRoster.easy')}
                    </button>
                    <button
                        className={`${styles.toggleBtn} ${difficulty === "hard" ? styles.toggleBtnActive : ""}`}
                        onClick={() => setDifficulty("hard")}
                    >
                        {t('teamRoster.hard')}
                    </button>
                </div>
            </div>

            {/* ===== Scoreboard ===== */}
            <div className={styles.scoreboard}>
                <div className={styles.scoreTile}>
                    <div className={styles.scoreLabel}>{t('teamRoster.streak')}</div>
                    <div className={styles.scoreValue}>{streak}</div>
                </div>
                <div className={styles.scoreTile}>
                    <div className={styles.scoreLabel}>{t('teamRoster.best')}</div>
                    <div className={`${styles.scoreValue} ${styles.scoreValueBest}`}>{best}</div>
                </div>
                <div className={styles.scoreTile}>
                    <div className={styles.scoreLabel}>{t('teamRoster.lives')}</div>
                    <div className={styles.lives}>
                        {Array.from({ length: MAX_MISTAKES }).map((_, i) =>
                            i < MAX_MISTAKES - mistakes
                                ? <Favorite key={i} className={styles.lifeOn} sx={{ fontSize: 18 }} />
                                : <FavoriteBorder key={i} className={styles.lifeOff} sx={{ fontSize: 18 }} />
                        )}
                    </div>
                </div>
                <div className={styles.scoreTile}>
                    <div className={styles.scoreLabel}>{t('teamRoster.timeLeft')}</div>
                    <div className={`${styles.scoreValue} ${remaining <= 10 && phase === "playing" ? styles.scoreValueLow : ''}`}>{timeText}</div>
                </div>
            </div>

            {phase === "loading" && (
                <div className={styles.loading}>
                    {loadFailed
                        ? <>{t('gamesLeaderboard.loadFailed')} <button className={styles.nextBtn} onClick={() => loadRound(false)}>{t('gamesLeaderboard.retry')}</button></>
                        : t('teamRoster.loading')}
                </div>
            )}

            {round && phase !== "loading" && (
                <>
                    {/* ===== Team header ===== */}
                    <div className={styles.teamHeader}>
                        <TeamLogo logo={round.teamLogo} name={round.teamName} className={styles.crest} />
                        <div>
                            <div className={styles.teamName}>{round.teamName}</div>
                            <div className={styles.context}>
                                {t('common:seasonNum', { number: round.season })} · {formatDivision(round.division)}
                            </div>
                        </div>
                        <div className={styles.foundWrap}>
                            <div className={styles.foundLabel}>{t('teamRoster.found')}</div>
                            <div className={styles.foundValue}>{found.length} / {round.slots}</div>
                        </div>
                    </div>

                    {/* ===== Roster slots ===== */}
                    <div className={styles.slots}>
                        {Array.from({ length: round.slots }).map((_, i) => {
                            const c = round.candidates.find((x) => x.steamId === found[i]);
                            if (!c) {
                                return (
                                    <div key={i} className={styles.slot}>
                                        <PersonOutline className={styles.slotEmptyIcon} sx={{ fontSize: 22 }} />
                                        <span className={styles.slotEmptyLabel}>{t('teamRoster.playerSlot', { n: i + 1 })}</span>
                                    </div>
                                );
                            }
                            return (
                                <div key={i} className={`${styles.slot} ${styles.slotFilled}`}>
                                    <div className={styles.slotPortrait}>
                                        <PlayerImage id={c.steamId} width={160} imgStyle={coverStyle} />
                                        <CheckCircle className={styles.slotCheck} sx={{ fontSize: 17 }} />
                                    </div>
                                    <span className={styles.slotName}>{c.name}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* ===== Candidate pool ===== */}
                    <div className={styles.poolLabel}>{t('teamRoster.pickPlayers')}</div>
                    <div className={styles.pool}>
                        {round.candidates.map((c) => (
                            <button
                                key={c.steamId}
                                className={candidateClass(c)}
                                onClick={() => handlePick(c.steamId)}
                                disabled={over || found.includes(c.steamId) || wrong.has(c.steamId) || busy}
                            >
                                <div className={styles.candPortrait}>
                                    <PlayerImage id={c.steamId} width={240} imgStyle={coverStyle} />
                                    {candidateBadge(c)}
                                    <div className={styles.candName}>{c.name}</div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* ===== Result ===== */}
                    {over && (
                        <div className={`${styles.resultBlock} ${phase === "won" ? styles.resultWon : styles.resultLost}`}>
                            <div className={`${styles.resultMsg} ${phase === "won" ? styles.resultMsgWon : styles.resultMsgLost}`}>
                                {phase === "won"
                                    ? <EmojiEvents sx={{ fontSize: 24 }} />
                                    : <SentimentDissatisfied sx={{ fontSize: 24 }} />}
                                {phase === "won"
                                    ? t('teamRoster.won', { team: round.teamName })
                                    : t(timeUp ? 'teamRoster.timeUp' : 'teamRoster.lost', { team: round.teamName })}
                            </div>
                            <div>
                                <button className={styles.nextBtn} onClick={() => loadRound(false)}>
                                    <Refresh sx={{ fontSize: 19 }} />
                                    {t('teamRoster.nextTeam')}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <GameLeaderboardPanel game="team-roster" variant={difficulty} refreshKey={boardKey} />
        </div>
    );
};

export default TeamRosterPage;
